
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { JournalEntries } from "@/components/journal/JournalEntries";
import { JournalAnalytics } from "@/components/journal/analytics/JournalAnalytics";
import { SymptomAnalytics } from "@/components/journal/analytics/SymptomAnalytics";
import { HealthJournal } from "@/types/database.types";
import { useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { 
  MessageSquare, Receipt, 
  CalendarDays, FileText, 
  ScrollText
} from "lucide-react";

interface JournalTabsProps {
  entries: HealthJournal[] | null;
  moodData: Array<{
    date: string;
    mood: number;
  }>;
  symptomsFrequency: Record<string, number>;
  isLoading: boolean;
  totalEntries: number;
}

export const JournalTabs = ({
  entries,
  moodData,
  symptomsFrequency,
  isLoading,
  totalEntries
}: JournalTabsProps) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const expressionChannel = supabase.channel('expression-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'expression_measurements'
      }, () => {
        queryClient.invalidateQueries({
          queryKey: ["expression-measurements"]
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(expressionChannel);
    };
  }, [queryClient]);

  const { data: messages } = useQuery({
    queryKey: ["patient-messages"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("messages")
        .select(`
          *,
          sender:profiles!sender_id(*),
          receiver:profiles!receiver_id(*)
        `)
        .or(`sender_id.eq.${user.id},receiver_id.eq.${user.id}`)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    }
  });

  const { data: appointments } = useQuery({
    queryKey: ["patient-appointments"],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Non authentifié");

      const { data, error } = await supabase
        .from("professional_appointments")
        .select(`
          *,
          professional:profiles!professional_id(*),
          service:professional_services(*)
        `)
        .eq("patient_id", user.id)
        .order("requested_date", { ascending: true });

      if (error) throw error;
      return data;
    }
  });

  return (
    <div className="rounded-lg border bg-card text-card-foreground shadow-sm">
      <Tabs defaultValue="journal" className="w-full">
        <TabsList className="w-full justify-start border-b px-6">
          <TabsTrigger value="journal" className="flex items-center gap-2">
            <ScrollText className="h-4 w-4" />
            Journal
          </TabsTrigger>
          <TabsTrigger value="messages" className="flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Messages
          </TabsTrigger>
          <TabsTrigger value="receipts" className="flex items-center gap-2">
            <Receipt className="h-4 w-4" />
            Reçus
          </TabsTrigger>
          <TabsTrigger value="appointments" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Rendez-vous
          </TabsTrigger>
          <TabsTrigger value="documents" className="flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Documents
          </TabsTrigger>
        </TabsList>

        <TabsContent value="journal" className="p-6">
          <JournalEntries entries={entries} isLoading={isLoading} />
        </TabsContent>

        <TabsContent value="messages" className="p-6 space-y-4">
          {messages?.map(message => (
            <div key={message.id} className="p-4 rounded-lg border bg-card">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium">
                    {message.sender?.full_name || "Utilisateur"}
                  </p>
                  <p className="text-sm mt-1">{message.content}</p>
                  <p className="text-xs text-muted-foreground mt-2">
                    {new Date(message.created_at).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="appointments" className="p-6 space-y-4">
          {appointments?.map(appointment => (
            <div key={appointment.id} className="p-4 rounded-lg border bg-card">
              <div className="flex justify-between items-start gap-4">
                <div className="flex-1">
                  <p className="font-medium">
                    Dr. {appointment.professional?.full_name}
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    {appointment.service?.service_name}
                  </p>
                  <p className="text-sm mt-1">
                    {new Date(appointment.requested_date).toLocaleDateString('fr-FR', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                      {appointment.status === "confirmed" ? "Confirmé" : "En attente"}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </TabsContent>

        <TabsContent value="receipts" className="p-6">
          <div className="text-muted-foreground">
            Aucun reçu disponible pour le moment.
          </div>
        </TabsContent>

        <TabsContent value="documents" className="p-6">
          <div className="text-muted-foreground">
            Aucun document disponible pour le moment.
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
